import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Col, Row } from "reactstrap";
import {
  getFormattedContactNumber,
  getModifiedObject,
  Toast,
  uploadImage,
} from "utility/helpers";
import { addCongressman, editCongDetails } from "services/services.congressman";
import { APP_URL } from "config";
import {
  NINETEENTH_CONGRESS,
  TWENTIETH_CONGRESS,
  CONGRESS_OPTIONS,
} from "utility/constants";
import ImgContainer from "components/common/img";
import defaultUserImage from "assets/img/default_user.png";
import { FILE_TYPE_IMAGE } from "constants/fileType";
import {
  HeaderText,
  ImageWrapper,
  UploadButton,
  UploadInput,
} from "assets/js/Styles";
import styled from "styled-components";
import useDistricts from "hooks/useDistricts";
import usePartylists from "hooks/usePartylists";
import CongressmanForm from "./CongressmanForm";
import { validateCongressmanForm } from "./utility";

const Wrapper = styled.div`
  margin-top: 50px;
`;

const FormSection = styled.div`
  margin-top: 44px;
  padding-bottom: 4rem;
`;

const HeaderActions = styled.div`
  margin-top: 24px;
  display: grid;
  grid-auto-flow: column;
  justify-content: space-between;
  align-items: center;
`;

const EditCongressman = () => {
  const location = useLocation();
  const cong = location.state?.cong || null;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedPartylist, setSelectedPartylist] = useState(null);

  const { options: districtOptions } = useDistricts();
  const { options: partylists } = usePartylists();

  const [formFields, setFormFields] = useState({
    lastname: "",
    firstname: "",
    middleInitial: "",
    mobile: "",
    district: "",
    party: "",
    cos: "",
    cosNumber: "",
    photo: "",
    photoName: "",
    cosFirstName: "",
    cosLastName: "",
    congress: [],
  });

  const navigate = useNavigate();

  const setCongDetails = () => {
    const selectedCongresses = [];

    if (cong.is19th) {
      selectedCongresses.push(NINETEENTH_CONGRESS);
    }

    if (cong.is20th) {
      selectedCongresses.push(TWENTIETH_CONGRESS);
    }

    const congressOptions = CONGRESS_OPTIONS.filter((opt) =>
      selectedCongresses.includes(opt.value)
    );

    const district = districtOptions.find(
      (dist) => dist.id === cong.DistrictId
    );

    const party = partylists.find((partylist) => partylist.id === cong.PartyId);

    setIsLoading(false);

    setFormFields({
      ...formFields,
      lastname: cong.lastName,
      firstname: cong.firstName,
      middleInitial: cong.middleInitial,
      mobile: getFormattedContactNumber(cong.contact),
      district,
      party,
      cos: cong.cosName,
      cosNumber: getFormattedContactNumber(cong.cosContact),
      photo: cong.photo,
      cosFirstName: cong.cosFirstName || cong.cosName,
      cosLastName: cong.cosLastName,
      congress: congressOptions,
    });

    if (cong.photo) {
      const imgContainer = document.querySelector(".cong-img");
      imgContainer.src = cong.photo;
    }
  };

  const sendData = (formData) => {
    setIsLoading(true);

    if (cong) {
      editCongDetails({
        url: `${APP_URL}/congressman/${cong.id}`,
        formData,
        callback: ({ isSuccess, msg }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/directory");

            Toast({
              type: 1,
              content: "Successfully edited cong details",
            });
          } else {
            Toast({
              type: 4,
              content: msg,
            });
          }
        },
      });
    } else {
      addCongressman({
        formData,
        callback: ({ isSuccess, msg }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/directory");

            Toast({
              type: 1,
              content: "Successfully added congressman.",
            });
          } else {
            Toast({
              type: 4,
              content: msg,
            });
          }
        },
      });
    }
  };

  const handleOnSubmit = async () => {
    const isValid = await validateCongressmanForm(formFields);

    if (typeof isValid === "boolean") {
      const data = {
        // eslint-disable-next-line max-len
        fullName: `HON. ${formFields.firstname} ${
          formFields.middleInitial ? `${formFields.middleInitial}. ` : ""
        }${formFields.lastname}`,
        middleInitial: formFields.middleInitial,
        firstName: formFields.firstname,
        lastName: formFields.lastname,
        contact: formFields.mobile?.slice(1),
        DistrictId: formFields.district.value,
        PartyId: formFields.party.value,
        cosName: `${formFields.cosFirstName} ${formFields.cosLastName}`,
        cosFirstName: formFields.cosFirstName,
        cosLastName: formFields.cosLastName,
        congress: formFields.congress
          ?.map((congress) => congress?.value?.trim())
          .join(";"),
      };

      if (formFields.cosNumber) {
        data.cosContact = formFields.cosNumber?.slice(1);
      }

      let initialData = {};

      if (cong) {
        const selectedCongresses = [];
        if (cong.is19th) selectedCongresses.push(NINETEENTH_CONGRESS);
        if (cong.is20th) selectedCongresses.push(TWENTIETH_CONGRESS);

        initialData = {
          middleInitial: cong.middleInitial,
          firstName: cong.firstName,
          lastName: cong.lastName,
          fullName: cong.fullName,
          contact: cong.contact,
          DistrictId: cong.DistrictId,
          PartyId: cong.PartyId,
          cosName: cong.cosName,
          cosContact: cong.cosContact,
          cosFirstName: cong.cosFirstName,
          cosLastName: cong.cosLastName,
          photo: cong.photo,
          congress: selectedCongresses.join(";"),
        };
      }

      setIsLoading(true);

      if (typeof formFields.photo !== "string") {
        const fileName = await uploadImage({
          photo: formFields.photo,
          photoName: formFields.photoName,
        });
        data.photo = fileName;
      }

      const formData = cong ? await getModifiedObject(initialData, data) : data;

      sendData(formData);
    } else {
      Toast({
        type: 4,
        content: isValid,
      });
    }
  };

  const handleFileUpload = (evt) => {
    const file = evt.target.files[0];
    const reader = new FileReader();
    const fileTye = file.type.split("/")[0];

    if (fileTye === FILE_TYPE_IMAGE) {
      reader.onloadend = () => {
        const imgContainer = document.querySelector(".cong-img");

        const fileName = cong
          ? `${
              cong.id
            }-${formFields.lastname.toLocaleLowerCase()}-${Date.now()}.${
              file.type.split("/")[1]
            }`
          : `${formFields.lastname.toLocaleLowerCase()}-${Date.now()}.${
              file.type.split("/")[1]
            }`;

        setFormFields({
          ...formFields,
          photo: file,
          photoName: fileName,
        });

        imgContainer.src = reader.result;
      };

      reader.readAsDataURL(file);
    } else {
      Toast({
        type: 4,
        content: "File uploaded must be an image.",
      });
    }
  };

  const handleCongressSelect = (options) => {
    setFormFields({
      ...formFields,
      congress: options,
    });
  };

  useEffect(() => {
    if (cong !== null && partylists.length && districtOptions.length) {
      setCongDetails();
    }
  }, [districtOptions, partylists, cong]);

  return (
    <Wrapper>
      <Col className="container">
        <HeaderActions>
          <HeaderText>
            {!cong ? "Add Congressman" : "Congressman Details"}
          </HeaderText>
        </HeaderActions>

        <FormSection>
          <Row>
            <Col
              xs="12"
              sm="6"
              lg="4"
            >
              <ImageWrapper>
                <ImgContainer
                  alt="cong-img"
                  className="cong-img"
                  src={defaultUserImage}
                  width="196px"
                  height="196px"
                />

                <UploadButton htmlFor="file-upload">
                  {"Upload Photo"}
                  <UploadInput
                    id="file-upload"
                    type="file"
                    onChange={handleFileUpload}
                  />
                </UploadButton>
              </ImageWrapper>
            </Col>
            <Col
              xs="12"
              sm="6"
              lg="8"
            >
              <CongressmanForm
                handleOnSubmit={handleOnSubmit}
                formFields={formFields}
                setFormFields={setFormFields}
                districtOptions={districtOptions}
                selectedDistrict={selectedDistrict}
                setSelectedDistrict={setSelectedDistrict}
                partylists={partylists}
                selectedPartylist={selectedPartylist}
                setSelectedPartylist={setSelectedPartylist}
                handleCongressSelect={handleCongressSelect}
                isLoading={isLoading}
              />
            </Col>
          </Row>
        </FormSection>
      </Col>
    </Wrapper>
  );
};

export default EditCongressman;
